/**
 * Utility functions for token management and validation
 */

/**
 * Parse JWT token payload
 */
export const parseJWTToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseJWTToken(token);
    if (!payload || !payload.exp) {
      return true;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Check if a token will expire within the specified minutes
 */
export const isTokenExpiringSoon = (token: string, minutesThreshold: number = 5): boolean => {
  try {
    const payload = parseJWTToken(token);
    if (!payload || !payload.exp) {
      return true;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const thresholdTime = currentTime + (minutesThreshold * 60);
    return payload.exp < thresholdTime;
  } catch (error) {
    console.error('Error checking token expiration threshold:', error);
    return true;
  }
};

/**
 * Get token expiration time as a Date object
 */
export const getTokenExpirationDate = (token: string): Date | null => {
  try {
    const payload = parseJWTToken(token);
    if (!payload || !payload.exp) {
      return null;
    }
    
    return new Date(payload.exp * 1000);
  } catch (error) {
    console.error('Error getting token expiration date:', error);
    return null;
  }
};
